import { useState, useCallback } from 'react';

export function useHistory<T>(initialState: T) {
  const [history, setHistory] = useState<T[]>([initialState]);
  const [pointer, setPointer] = useState<number>(0);

  const setState = useCallback(
    (newState: T | ((prev: T) => T)) => {
      setHistory((prev) => {
        const current = prev[pointer];
        const resolvedState =
          typeof newState === 'function' ? (newState as Function)(current) : newState;
        
        // If state hasn't changed, don't add to history
        if (JSON.stringify(current) === JSON.stringify(resolvedState)) {
          return prev;
        }

        const newHistory = prev.slice(0, pointer + 1);
        newHistory.push(resolvedState);
        
        // Limit history to 50 items to prevent memory leaks
        if (newHistory.length > 50) {
          newHistory.shift();
          setPointer(newHistory.length - 1);
          return newHistory;
        }
        
        setPointer(newHistory.length - 1);
        return newHistory;
      });
    },
    [pointer]
  );

  const undo = useCallback(() => {
    setPointer((prev) => Math.max(0, prev - 1));
  }, []);

  const redo = useCallback(() => {
    setPointer((prev) => Math.min(history.length - 1, prev + 1));
  }, [history.length]);

  const resetState = useCallback((state: T) => {
    setHistory([state]);
    setPointer(0);
  }, []);

  return {
    state: history[pointer] !== undefined ? history[pointer] : initialState,
    setState,
    undo,
    redo,
    canUndo: pointer > 0,
    canRedo: pointer < history.length - 1,
    resetState,
  };
}
